// Game state construction and the derived queries the engine leans on.
//
// Everything that asks "what is true right now" lives here; everything that
// changes the game lives in engine.ts. Keeping the split sharp is what makes the
// engine testable -- these functions are pure, so a test can assert on Might or
// legality without driving a whole turn.

import type { CardIndex } from './cards.ts';
import { isUnitType, keywordValue } from './cards.ts';
import type {
  Ability, CardScript, Condition, DynamicAmount, PassiveEffect, ScriptIndex, ObjectFilter,
} from './effects.ts';
import { EMPTY_SCRIPT } from './effects.ts';
import type {
  BattlefieldState, CardDef, Cost, Domain, GameObject, GameState, Keyword,
  Location, ObjectId, PlayerId, RunePool, SpendOnly, SpendPurpose, TurnEffect,
  Zone, ZoneKind,
} from './types.ts';
import { DOMAINS, sameLocation } from './types.ts';

export const OPPONENT = (p: PlayerId): PlayerId => (p === 0 ? 1 : 0);

export const emptyPool = (): RunePool => ({
  energy: 0,
  power: { fury: 0, calm: 0, mind: 0, body: 0, chaos: 0, order: 0 },
  any: 0,
  restricted: [],
});

// -- zone access -------------------------------------------------------------

type ListZone = Exclude<ZoneKind, 'board' | 'chain' | 'facedown'>;

export function zoneList(state: GameState, kind: ListZone, player: PlayerId): number[] {
  return state.zones[kind][player];
}

/**
 * 811. Cards hidden facedown at a battlefield.
 *
 * The facedown zone has no list of its own -- a hidden card is found by its
 * zone, which also records which battlefield it is under.
 */
export function facedownAt(
  state: GameState, battlefield: number, player?: PlayerId,
): GameObject[] {
  const out: GameObject[] = [];
  for (const id of Object.keys(state.objects)) {
    const o = state.objects[Number(id)];
    if (!o || o.zone.kind !== 'facedown' || o.zone.battlefield !== battlefield) continue;
    if (player !== undefined && o.controller !== player) continue;
    out.push(o);
  }
  return out;
}

/** 811.1.b. The printed cost to hide a card: one Power of any domain. */
export const HIDE_COST: Cost = { energy: 0, power: 1, powerDomains: [] };

/**
 * 811.1.b. What this player actually pays to Hide.
 *
 * A permanent can replace the printed [A] with something else, so the price is
 * asked for rather than assumed wherever hiding is offered or performed.
 */
export function hideCostFor(
  state: GameState, rules: Rules, player: PlayerId,
): Cost {
  // 811. "You can hide cards ignoring costs this turn" beats any standing
  // discount: free is free.
  if (state.turnEffects.some((e) => e.k === 'freeHide' && e.player === player)) {
    return { energy: 0, power: 0, powerDomains: [] };
  }
  for (const source of boardObjects(state)) {
    if (source.controller !== player) continue;
    for (const effect of activePassives(state, rules, source)) {
      if (effect.p !== 'hideCost') continue;
      return { energy: effect.energy, power: effect.power, powerDomains: [] };
    }
  }
  return HIDE_COST;
}

export function removeFromZone(state: GameState, obj: GameObject): void {
  const z = obj.zone;
  if (z.kind === 'board' || z.kind === 'chain' || z.kind === 'facedown') return;
  const list = zoneList(state, z.kind, z.player);
  const i = list.indexOf(obj.id);
  if (i >= 0) list.splice(i, 1);
}

/**
 * Moves an object to a new zone. 124.2: crossing into or out of a non-board
 * zone makes it a new object for tracking purposes, so every temporary
 * modification, status and damage marker is dropped.
 */
export function moveToZone(
  state: GameState, rules: Rules, obj: GameObject, zone: Zone,
  opts: { toBottom?: boolean } = {},
): void {
  const wasOnBoard = obj.zone.kind === 'board';
  const cameFrom = obj.location;

  // 447. "If a card would go to your trash from anywhere other than your Main
  // Deck, banish it instead." A replacement on the destination, so it is
  // applied here rather than at each of the places that send a card away --
  // there are dozens, and one of them forgetting is a card that half works.
  if (zone.kind === 'trash' && obj.zone.kind !== 'mainDeck') {
    const whose = zone.player;
    const replaced = boardObjects(state).some((o) =>
      activePassives(state, rules, o).some((e) => e.p === 'trashBecomesBanish'
        && (e.who === 'you' ? o.controller === whose : o.controller !== whose)));
    if (replaced) zone = { kind: 'banishment', player: whose };
  }

  removeFromZone(state, obj);
  obj.zone = zone;

  // 375. "When a unit here is returned to a player's hand." Announced before
  // the zone change wipes the location the watcher compares against.
  if (wasOnBoard && zone.kind === 'hand') {
    state.pendingTriggers.push({
      on: 'returnedToHand', player: zone.player, subject: obj.id,
      ...(cameFrom ? { from: cameFrom } : {}),
    });
  }

  const nowOnBoard = zone.kind === 'board';
  if (wasOnBoard !== nowOnBoard || (!wasOnBoard && !nowOnBoard)) {
    obj.damage = 0;
    obj.statuses = [];
    obj.mods = [];
    obj.exhausted = false;
    obj.movesThisTurn = 0;
    obj.conqueredThisTurn = false;
    obj.damagedThisTurn = false;
    obj.usedThisTurn = [];
    // 056.2: a card always ends up in its owner's zone, never an opponent's.
    obj.controller = obj.owner;
    detachAll(state, obj);
  }
  if (!nowOnBoard) obj.location = null;

  if (zone.kind !== 'board' && zone.kind !== 'chain' && zone.kind !== 'facedown') {
    const list = zoneList(state, zone.kind, zone.player);
    if (opts.toBottom) list.unshift(obj.id);
    else list.push(obj.id);
  }
  obj.enteredTurn = state.turn;
  state.version++;
}

function detachAll(state: GameState, obj: GameObject): void {
  for (const id of obj.attachments) {
    const a = state.objects[id];
    if (a) a.attachedTo = null;
  }
  obj.attachments = [];
  if (obj.attachedTo !== null) {
    const host = state.objects[obj.attachedTo];
    if (host) host.attachments = host.attachments.filter((x) => x !== obj.id);
    obj.attachedTo = null;
  }
}

// -- board queries -----------------------------------------------------------

export function boardObjects(state: GameState): GameObject[] {
  const out: GameObject[] = [];
  for (const key in state.objects) {
    const o = state.objects[key as unknown as number]!;
    if (o.zone.kind === 'board') out.push(o);
  }
  return out;
}

export function objectsAt(state: GameState, loc: Location): GameObject[] {
  return boardObjects(state).filter((o) => sameLocation(o.location, loc));
}

export function unitsAt(state: GameState, index: CardIndex, loc: Location): GameObject[] {
  return objectsAt(state, loc).filter((o) => isUnitType(index.get(o.card).type));
}

export function unitsOf(
  state: GameState, index: CardIndex, player: PlayerId, loc?: Location,
): GameObject[] {
  return boardObjects(state).filter((o) =>
    o.controller === player
    && isUnitType(index.get(o.card).type)
    && (!loc || sameLocation(o.location, loc)));
}

export const battlefieldLocation = (i: number): Location => ({ kind: 'battlefield', index: i });
export const baseLocation = (p: PlayerId): Location => ({ kind: 'base', player: p });

/**
 * 164.2. Whether an activation spends its own source rather than exhausting it.
 *
 * The two rune taps are not interchangeable: `[E]` exhausts a rune for Energy
 * and it readies again next turn, while "Recycle this" sends it to the Rune
 * Deck for Power and it is gone for the game. Both are "abilities that add
 * resources", so anything choosing between them has to be able to tell them
 * apart -- the client's payment search and the AI both do, from here.
 */
export function tapIsDestructive(
  state: GameState, rules: Rules, action: { object: ObjectId; ability: string },
): boolean {
  const source = state.objects[action.object];
  if (!source) return false;
  for (const ability of abilitiesOf(rules, source.card)) {
    if (ability.kind !== 'activated' || ability.key !== action.ability) continue;
    return Boolean(ability.cost.recycleSelf) || Boolean(ability.cost.killSelf)
      || Boolean(ability.cost.banishSelf);
  }
  return false;
}

// -- scripts and abilities ---------------------------------------------------

export interface Rules {
  index: CardIndex;
  scripts: ScriptIndex;
}

export function scriptFor(rules: Rules, cardId: string): CardScript {
  return rules.scripts[cardId] ?? EMPTY_SCRIPT;
}

/**
 * 817/823. The abilities a keyword stands for, when it stands for a whole one.
 *
 * These have no printed text of their own -- the reminder is stripped before
 * the grammar sees the card -- so nothing else builds them.
 */
function keywordShorthand(kw: Keyword, value: number): Ability[] {
  // 817.1.b. Vision: "when this is played, predict".
  if (kw === 'vision') {
    return [{
      kind: 'triggered', trigger: { on: 'played', self: true },
      effects: [{ op: 'predict' }],
    }];
  }
  // 821.1.c. Weaponmaster: "when you play me, you may pay one of your
  // Equipment's Equip cost, reduced by [A], to attach it to me".
  if (kw === 'weaponmaster') {
    return [{
      kind: 'triggered', trigger: { on: 'played', self: true }, optional: true,
      effects: [{ op: 'equipDiscounted', discount: 1 }],
    }];
  }
  // 823.1.c.1. Hunt X: "when I conquer or hold, gain X XP" -- both events, as
  // two abilities, which is how 382 describes them.
  if (kw === 'hunt') {
    return [
      { kind: 'triggered', trigger: { on: 'conquer' }, effects: [{ op: 'gainXp', amount: value }] },
      { kind: 'triggered', trigger: { on: 'hold' }, effects: [{ op: 'gainXp', amount: value }] },
    ];
  }
  return [];
}

export function abilitiesOf(rules: Rules, cardId: string): Ability[] {
  return scriptFor(rules, cardId).abilities;
}

/**
 * 363. The abilities an object has right now: the ones its card prints, plus
 * any another permanent is handing it (`units here have "[E]: gain 1 XP."`).
 *
 * Separate from `abilitiesOf` because that one answers about a *card* and is
 * used where no object exists -- reading a cost out of the index, say. Only
 * the paths that act on a board object can see granted abilities, which is
 * exactly where they apply.
 */
export function abilitiesOn(state: GameState, rules: Rules, obj: GameObject): Ability[] {
  const printed = abilitiesOf(rules, obj.card);
  let granted: Ability[] | null = null;

  // 817/823. Some keywords *are* a triggered ability in shorthand, and a card
  // can be given one by another card ("your Mechs have [Vision]"). Built from
  // the keywords the object has right now, so a granted keyword brings its
  // ability with it rather than being an inert word.
  for (const kw of keywordsOf(state, rules, obj)) {
    for (const a of keywordShorthand(kw, keywordAmount(state, rules, obj, kw))) {
      (granted ??= []).push(a);
    }
  }
  // 363.3. An ability lent for the turn rather than by a standing passive.
  for (const e of state.turnEffects) {
    if (e.k !== 'grantedAbility' || e.object !== obj.id) continue;
    (granted ??= []).push({
      ...e.ability,
      key: `lent:${obj.id}:${e.ability.kind === 'activated' ? e.ability.key : 't'}`,
    } as Ability);
  }

  // 371. "I have all [E] abilities of all friendly legends, units, and gear."
  // Read off the *other* permanents rather than pushed from them, and only
  // from their printed lists: going through `abilitiesOn` again would let two
  // cards with this passive borrow each other's borrowings forever.
  for (const effect of activePassives(state, rules, obj)) {
    if (effect.p !== 'gainsAbilitiesOf') continue;
    for (const other of boardObjects(state)) {
      if (other.id === obj.id) continue;
      if (!matchesFilter(state, rules, other, effect.filter, obj)) continue;
      for (const a of abilitiesOf(rules, other.card)) {
        // 818. "Copy that unit's text" takes everything printed; "all [E]
        // abilities" takes only the ones with that price.
        if (effect.only === 'exhaust' && (a.kind !== 'activated' || !a.cost.exhaust)) continue;
        if (a.kind === 'spell') continue;
        // Keyed by the card it was borrowed from, so the same ability taken
        // off two permanents keeps two once-per-turn flags. Only activated
        // abilities carry a key; the rest are identified by position.
        (granted ??= []).push(a.kind === 'activated'
          ? { ...a, key: `borrowed:${other.id}:${a.key}` }
          : a);
      }
    }
  }

  for (const source of boardObjects(state)) {
    for (const effect of activePassives(state, rules, source)) {
      if (effect.p !== 'grantsAbility') continue;
      if (!matchesFilter(state, rules, obj, effect.filter, source)) continue;
      // Keyed apart from the printed ones so a granted ability's once-per-turn
      // flag cannot collide with a printed ability of the same key.
      (granted ??= []).push({
        ...effect.ability,
        key: `granted:${source.id}:${effect.ability.key}`,
      });
    }
  }
  return granted ? [...printed, ...granted] : printed;
}

/**
 * Keywords an object has right now: printed, plus anything granted by a
 * temporary modification or by another permanent's aura (801.3.a).
 */
export function keywordsOf(state: GameState, rules: Rules, obj: GameObject): Set<Keyword> {
  const def = rules.index.get(obj.card);
  const out = new Set<Keyword>(def.keywords);
  for (const m of obj.mods) {
    if (m.kind === 'keyword' && m.keyword) out.add(m.keyword);
  }

  // Re-entry: an aura can be filtered on a keyword ("friendly units with
  // [Temporary] have ..."), and asking whether this object has that keyword
  // walks the auras again. The inner answer is the printed and granted set,
  // which is everything that needs no other object to know.
  if (computingKeywords.has(obj.id)) return out;
  computingKeywords.add(obj.id);
  try {
    for (const { effect } of aurasAffecting(state, rules, obj)) {
      if (effect.p === 'keywordAura') out.add(effect.keyword);
    }
    // "[Empowered] I have [Tank]" grants to the source rather than to a filter.
    for (const effect of activePassives(state, rules, obj)) {
      if (effect.p === 'selfKeyword') out.add(effect.keyword);
    }
  } finally {
    computingKeywords.delete(obj.id);
  }
  return out;
}

export function hasKeywordNow(
  state: GameState, rules: Rules, obj: GameObject, kw: Keyword,
): boolean {
  return keywordsOf(state, rules, obj).has(kw);
}

/** Summed value of a keyword: printed plus every granted instance (807.2). */
export function keywordAmount(
  state: GameState, rules: Rules, obj: GameObject, kw: Keyword,
): number {
  const def = rules.index.get(obj.card);
  let total = keywordValue(def, kw);
  for (const m of obj.mods) {
    if (m.kind === 'keyword' && m.keyword === kw) total += m.amount || 1;
  }
  for (const { effect } of aurasAffecting(state, rules, obj)) {
    if (effect.p === 'keywordAura' && effect.keyword === kw) total += effect.value ?? 1;
  }
  for (const effect of activePassives(state, rules, obj)) {
    if (effect.p !== 'selfKeyword' || effect.keyword !== kw) continue;
    // "[Assault] equal to the number of enemy units here" changes as the board
    // does, so it is counted now rather than baked in when the card compiled.
    total += effect.valueFrom
      ? countDynamic(state, rules, obj, obj.controller, effect.valueFrom)
      : (effect.value ?? 1);
  }
  return total;
}

/**
 * A quantity an effect scales off ("+1 [M] for each point you have").
 *
 * This lives here rather than beside the interpreter because passives need it
 * too, and a second copy would be free to drift from the first.
 */
/**
 * 133.8. Every tag an object has: the ones its card prints, the ones granted
 * during the game, and the ones a passive on the card itself states ("I am a
 * Mech").
 *
 * The printed ability list is read directly rather than through
 * `activePassives`: a tag feeds `matchesFilter`, and `activePassives` can gate
 * a passive on a condition that asks a filter -- which would recurse. Nothing
 * prints a *conditional* tag, so the unconditional read is exact.
 */
export function tagsOf(rules: Rules, obj: GameObject): readonly string[] {
  const printed = rules.index.get(obj.card).tags;
  const extra: string[] = obj.tags ? [...obj.tags] : [];
  for (const ability of abilitiesOf(rules, obj.card)) {
    if (ability.kind !== 'passive') continue;
    for (const e of ability.effects) if (e.p === 'selfTag') extra.push(e.tag);
  }
  return extra.length === 0 ? printed : [...printed, ...extra];
}

/** How many of `tags` appear on at least one unit this player controls. */
function distinctTags(
  state: GameState, rules: Rules, controller: PlayerId, tags: string[],
): number {
  let found = 0;
  for (const tag of tags) {
    const has = boardObjects(state).some((o) =>
      o.controller === controller
      && isUnitType(rules.index.get(o.card).type)
      && tagsOf(rules, o).includes(tag));
    if (has) found++;
  }
  return found;
}

export function countDynamic(
  // 356. `source` is null when the question is asked of a card still in hand,
  // which has no object yet. Counts that compare against the source answer 0
  // rather than throwing.
  state: GameState, rules: Rules, source: GameObject | null,
  controller: PlayerId, from: DynamicAmount,
): number {
  if (typeof from === 'object') {
    if ('tagsAmong' in from) return distinctTags(state, rules, controller, from.tagsAmong);
    if ('selfDamageInstancesThisTurn' in from) return source?.damageInstancesThisTurn ?? 0;
    if (!source) return 0;
    // The filter form counts whatever is on the board and matches.
    return boardObjects(state).filter((o) =>
      matchesFilter(state, rules, o, from.of, source)).length;
  }
  if (!source) {
    switch (from) {
      case 'cardsInTrash': return zoneList(state, 'trash', controller).length;
      case 'handSize': return zoneList(state, 'hand', controller).length;
      case 'points': return state.players[controller].points;
      case 'xp': return state.players[controller].xp;
      default: return 0;
    }
  }

  switch (from) {
    case 'attachedToSelf': return source.attachments.length;
    case 'selfEmpowerCount': return source?.empowerCount ?? 0;
    case 'sameNameInTrash': {
      if (!source) return 0;
      const name = rules.index.get(source.card).name;
      return zoneList(state, 'trash', controller).filter((id) => {
        const o = state.objects[id];
        return o !== undefined && rules.index.get(o.card).name === name;
      }).length;
    }
    case 'highestFriendlyMight': {
      let best = 0;
      for (const o of boardObjects(state)) {
        if (o.controller !== controller) continue;
        if (!isUnitType(rules.index.get(o.card).type)) continue;
        best = Math.max(best, effectiveMight(state, rules, o));
      }
      return best;
    }
    case 'selfMight': return effectiveMight(state, rules, source);
    case 'points': return state.players[controller].points;
    case 'xp': return state.players[controller].xp;
    case 'handSize': return zoneList(state, 'hand', controller).length;
    case 'friendlyUnits':
      return boardObjects(state).filter((o) =>
        o.controller === controller && isUnitType(rules.index.get(o.card).type)).length;
    case 'enemyUnits':
      return boardObjects(state).filter((o) =>
        o.controller !== controller && isUnitType(rules.index.get(o.card).type)).length;
    // 143.2. A unit is Mighty while it has 5 or more Might.
    case 'mightyUnits':
      return boardObjects(state).filter((o) =>
        o.controller === controller
        && isUnitType(rules.index.get(o.card).type)
        && effectiveMight(state, rules, o) >= 5).length;
    case 'buffedUnits':
      return boardObjects(state).filter((o) =>
        o.controller === controller && o.statuses.includes('buffed')).length;
    case 'friendlyRunes':
      return boardObjects(state).filter((o) =>
        o.controller === controller && rules.index.get(o.card).type === 'rune').length;
    case 'cardsPlayedThisTurn':
      return state.players[controller].cardsPlayedThisTurn;
    case 'pointsHeldThisTurn':
      return state.players[controller].pointsHeldThisTurn;
    case 'selfCost':
      return rules.index.get(source.card).cost.energy;
    case 'chosenCost':
    case 'chosenSpellCost':
      // Only the running effect knows what was chosen.
      return 0;
    case 'chosenMight':
      // Only the running effect knows what was chosen.
      return 0;
    case 'damageOnChosen':
      // Only the running effect knows what was chosen; the board cannot say.
      return 0;
    case 'paidAmount':
      // Only meaningful inside the instructions a payment introduced; the
      // interpreter supplies it, so the board cannot answer here.
      return 0;
    case 'excessDamage':
      return state.players[controller].excessDamageThisTurn;
    case 'tokenUnits':
      return boardObjects(state).filter((o) =>
        o.controller === controller && rules.index.get(o.card).type === 'token').length;
    case 'enemyUnitsHere':
      return boardObjects(state).filter((o) =>
        o.controller !== controller
        && isUnitType(rules.index.get(o.card).type)
        && source.location !== null
        && sameLocation(o.location, source.location)).length;
    case 'friendlyUnitsHere':
      return boardObjects(state).filter((o) =>
        o.controller === controller
        && isUnitType(rules.index.get(o.card).type)
        && source.location !== null
        && sameLocation(o.location, source.location)).length;
    case 'friendlyGear':
      return boardObjects(state).filter((o) =>
        o.controller === controller && rules.index.get(o.card).type === 'gear').length;
    case 'friendlyEquipment':
      return boardObjects(state).filter((o) =>
        o.controller === controller
        && rules.index.get(o.card).tags.includes('Equipment')).length;
    case 'cardsInTrash':
      return zoneList(state, 'trash', controller).length;
    case 'battlefieldsControlled':
      return state.battlefields.filter((b) => b.controller === controller).length;
    default: return 0;
  }
}

/**
 * Conditions that can be judged from the board alone.
 *
 * Returns null for the ones that only mean something inside a resolving effect
 * ("if you do", "if this kills it") -- the interpreter answers those itself.
 * A passive can only ever be gated on a static condition, so this is the whole
 * vocabulary `activePassives` needs.
 */
/**
 * Conditions `testStaticCondition` can judge with no source object -- the ones
 * about the player's own resources and the board, which is exactly what a card
 * still in hand can ask about itself.
 */
/**
 * Every condition kind something actually judges.
 *
 * The same guard as `IMPLEMENTED_PASSIVES` and `IMPLEMENTED_TRIGGERS`, and for
 * the same reason: a condition nothing reads is not a weaker card, it is a
 * wrong one -- the gate silently disappears and the effect applies for free.
 * A test asserts the generated data only uses kinds listed here.
 */
export const IMPLEMENTED_CONDITIONS = new Set<Condition['c']>([
  // Judged from the board alone, by `testStaticCondition`.
  'all', 'not', 'exists', 'countAtLeast', 'countExactly', 'totalMightAtLeast',
  'assignedExcess', 'championZoneEmpty', 'controlsBattlefield',
  'controlsSelfBattlefield', 'controlsFacedown', 'controlsTag',
  'discardedThisTurn', 'exactlyOneOtherUnitHere', 'fewerRunesThanOpponent',
  'gainedXpThisTurn', 'handAtLeast', 'handAtMost', 'handExactly', 'hasXp',
  'isPhase', 'isYourTurn', 'onFirstTurn', 'opponentControlsBattlefield',
  'opponentNearVictory', 'playedAnotherCardThisTurn', 'playedEachThisTurn',
  'playedThisTurn', 'runesAtLeast', 'runesAtMost', 'chosenEnemyThisTurn',
  'loneEnemyHere',
  'selfAlone', 'selfAtBattlefield', 'selfAttackingWithAnother', 'selfBuffed',
  'selfConqueredThisTurn', 'selfEmpowered', 'selfEmpoweredTimes',
  'selfInCombat', 'selfIsMighty', 'selfMightAtLeast', 'selfMovedAtLeast',
  'selfDamagedThisTurn',
  'selfNearVictory', 'selfPlayedFromHand', 'showdownHere', 'spentAtLeast',
  'unitDiedThisTurn', 'unitDiedInBeginningPhase', 'unitsHaveTags',
  // Judged only while an effect is running, by `testCondition`.
  'killedIt', 'didIt', 'paidAdditionalCost', 'revealedTag', 'chosenMightAtMost',
  'chosenAlone', 'attachedThisTurn', 'anyChosenIs',
  'chosenMightLessThanSelf', 'chosenIs', 'revealedIs', 'targetDied',
]);

const SOURCE_FREE_CONDITIONS = new Set<Condition['c']>([
  'hasXp', 'controlsBattlefield', 'opponentControlsBattlefield', 'handAtMost',
  'handAtLeast', 'handExactly', 'countExactly', 'all', 'unitDiedInBeginningPhase',
  'isYourTurn', 'controlsFacedown', 'runesAtMost', 'runesAtLeast',
  'unitsHaveTags', 'controlsTag', 'spentAtLeast', 'assignedExcess',
  'championZoneEmpty',
  'selfNearVictory',
  // Combinators: they recurse, and a null from inside propagates out, so a
  // gate wrapping a question about "me" is still correctly unanswerable.
  'not',
]);

export function testStaticCondition(
  // 356. Null when the card being asked about is still in hand and has no
  // object. Every condition about "me" is unanswerable then, and says so.
  state: GameState, rules: Rules, source: GameObject | null,
  controller: PlayerId, cond: Condition,
): boolean | null {
  // 356. Without an object, only the questions about the player and the board
  // can be answered; everything about "me" stays unknown.
  if (!source && !SOURCE_FREE_CONDITIONS.has(cond.c)) return null;
  const self = source as GameObject;
  switch (cond.c) {
    case 'chosenEnemyThisTurn':
      return state.players[controller].chosenEnemyThisTurn >= cond.count;
    case 'selfEmpoweredTimes':
      return (self.empowerCount ?? 0) >= cond.times;
    case 'selfEmpowered': return self.statuses.includes('empowered');
    case 'selfBuffed': return self.statuses.includes('buffed');
    case 'selfAtBattlefield': return self.location?.kind === 'battlefield';
    case 'selfIsMighty': return effectiveMight(state, rules, self) >= 5;
    case 'selfMightAtLeast': return effectiveMight(state, rules, self) >= cond.amount;
    case 'controlsBattlefield':
      return state.battlefields.some((b) => b.controller === controller);
    case 'opponentControlsBattlefield':
      return state.battlefields.some((b) => b.controller !== null && b.controller !== controller);
    case 'hasXp': return state.players[controller].xp >= cond.amount;
    case 'handAtMost': return zoneList(state, 'hand', controller).length <= cond.count;
    case 'handAtLeast': return zoneList(state, 'hand', controller).length >= cond.count;
    case 'handExactly': return zoneList(state, 'hand', controller).length === cond.count;
    case 'all':
      return cond.conds.every((c) =>
        testStaticCondition(state, rules, self, controller, c) === true);
    case 'countExactly':
      return boardObjects(state).filter((o) =>
        matchesFilter(state, rules, o, cond.filter, self)).length === cond.count;
    case 'discardedThisTurn': return zoneList(state, 'trash', controller).length > 0;
    case 'playedAnotherCardThisTurn':
      return state.players[controller].cardsPlayedThisTurn > 1;
    case 'controlsTag':
      return boardObjects(state).some((o) =>
        o.controller === controller && rules.index.get(o.card).tags.includes(cond.tag));
    case 'opponentNearVictory':
      return state.players.some((p) =>
        p.id !== controller && p.points >= state.config.victoryScore - cond.within);
    case 'not': {
      const inner = testStaticCondition(state, rules, self, controller, cond.cond);
      return inner === null ? null : !inner;
    }
    case 'selfNearVictory':
      return state.players[controller].points >= state.config.victoryScore - cond.within;
    case 'runesAtMost':
    case 'runesAtLeast': {
      const runes = boardObjects(state).filter((o) =>
        o.controller === controller && rules.index.get(o.card).type === 'rune').length;
      return cond.c === 'runesAtMost' ? runes <= cond.count : runes >= cond.count;
    }
    case 'spentAtLeast': {
      const spent = state.players[controller].spentThisTurn;
      return spent.energy + spent.power >= cond.energy + cond.power;
    }
    case 'assignedExcess':
      return state.players[controller].excessDamageThisTurn >= cond.amount;
    case 'exists':
      return boardObjects(state).some((o) =>
        matchesFilter(state, rules, o, cond.filter, self));
    case 'countAtLeast': {
      // A filter naming a zone off the board is asking about cards, not
      // permanents, so the pool widens to every object rather than silently
      // counting none.
      const pool = cond.filter.zone && cond.filter.zone !== 'board'
        ? Object.values(state.objects).filter((o): o is GameObject => Boolean(o))
        : boardObjects(state);
      return pool.filter((o) =>
        matchesFilter(state, rules, o, cond.filter, self)).length >= cond.count;
    }
    case 'unitDiedInBeginningPhase': {
      const mine = state.players[controller].unitsDiedInBeginningPhase;
      const theirs = state.players.reduce(
        (n, p) => n + (p.id === controller ? 0 : p.unitsDiedInBeginningPhase), 0);
      if (cond.side === 'friendly') return mine > 0;
      if (cond.side === 'enemy') return theirs > 0;
      return mine + theirs > 0;
    }
    case 'unitDiedThisTurn': {
      const mine = state.players[controller].unitsDiedThisTurn;
      const theirs = state.players.reduce(
        (n, p) => n + (p.id === controller ? 0 : p.unitsDiedThisTurn), 0);
      if (cond.side === 'friendly') return mine > 0;
      if (cond.side === 'enemy') return theirs > 0;
      return mine + theirs > 0;
    }
    case 'totalMightAtLeast': {
      let total = 0;
      for (const o of boardObjects(state)) {
        if (!self || !matchesFilter(state, rules, o, cond.filter, self)) continue;
        total += effectiveMight(state, rules, o);
      }
      return total >= cond.amount;
    }
    case 'selfPlayedFromHand':
      return self.playedFrom === 'hand';
    case 'selfConqueredThisTurn':
      // An Equipment asks about the unit it is on, not about itself.
      return self.attachedTo !== null
        ? (state.objects[self.attachedTo]?.conqueredThisTurn ?? false)
        : self.conqueredThisTurn;
    case 'gainedXpThisTurn':
      return state.players[controller].xpGainedThisTurn > 0;
    case 'fewerRunesThanOpponent': {
      const mine = boardObjects(state).filter((o) =>
        o.controller === controller && rules.index.get(o.card).type === 'rune').length;
      const theirs = boardObjects(state).filter((o) =>
        o.controller !== controller && rules.index.get(o.card).type === 'rune').length;
      return mine < theirs;
    }
    case 'playedEachThisTurn': {
      // Matched greedily against distinct cards: "a non-token unit, a non-token
      // gear and a spell" wants three cards, not one that happens to fit twice.
      const played = [...state.players[controller].playedThisTurn];
      for (const filter of cond.filters) {
        const at = played.findIndex((id) => matchesCardDef(rules.index.get(id), filter));
        if (at < 0) return false;
        played.splice(at, 1);
      }
      return true;
    }
    case 'playedThisTurn': {
      const asked = cond.who === 'opponent' ? OPPONENT(controller) : controller;
      const played = state.players[asked].playedThisTurn;
      let hits = 0;
      for (const cardId of played) {
        const def = rules.index.get(cardId);
        // Matched against the printed card, since the object may be long gone.
        if (matchesCardDef(def, cond.filter)) hits++;
      }
      return hits >= (cond.count ?? 1);
    }
    case 'unitsHaveTags':
      return distinctTags(state, rules, controller, cond.tags) >= cond.count;
    case 'controlsSelfBattlefield': {
      const bf = state.battlefields.find((b) => b.object === self.id);
      return bf !== undefined && bf.controller === controller;
    }
    case 'onFirstTurn':
      return state.players[controller].turnsTaken === 0;
    case 'isPhase':
      return state.phase === cond.phase && state.turnPlayer === controller;
    case 'championZoneEmpty':
      return zoneList(state, 'championZone', controller).length === 0;
    case 'controlsFacedown':
      return Object.values(state.objects).some((o) =>
        o !== undefined && o.zone.kind === 'facedown' && o.controller === controller);
    case 'selfMovedAtLeast':
      return self.movesThisTurn >= cond.count;
    case 'isYourTurn':
      return state.turnPlayer === controller;
    case 'selfAttackingWithAnother': {
      if (!self.statuses.includes('attacking')) return false;
      return boardObjects(state).some((o) =>
        o.id !== self.id && o.controller === self.controller
        && o.statuses.includes('attacking'));
    }
    case 'selfDamagedThisTurn':
      return self.damagedThisTurn === true;
    case 'selfInCombat':
      return self.statuses.includes('attacking') || self.statuses.includes('defending');
    case 'showdownHere': {
      if (state.showdownState !== 'showdown') return false;
      const at = self.location;
      if (at?.kind !== 'battlefield') return false;
      return state.battlefields[at.index]?.showdownStaged === true;
    }
    case 'selfAlone': {
      const here = boardObjects(state).filter((o) =>
        o.controller === self.controller
        && o.id !== self.id
        && isUnitType(rules.index.get(o.card).type)
        // A null location is not "the same place" as another null one.
        && self.location !== null
        && sameLocation(o.location, self.location));
      return here.length === 0;
    }
    case 'loneEnemyHere': {
      // 143. "An enemy unit is alone here": one enemy unit at this location and
      // no more. Says nothing about how many friends I have, which is what
      // `selfAlone` asks and what this used to be compiled onto.
      if (self.location === null) return false;
      const enemies = boardObjects(state).filter((o) =>
        o.controller !== self.controller
        && isUnitType(rules.index.get(o.card).type)
        && sameLocation(o.location, self.location));
      return enemies.length === 1;
    }
    case 'exactlyOneOtherUnitHere': {
      const here = boardObjects(state).filter((o) =>
        o.controller === self.controller
        && o.id !== self.id
        && isUnitType(rules.index.get(o.card).type)
        && self.location !== null
        && sameLocation(o.location, self.location));
      return here.length === 1;
    }
    default: return null;
  }
}

/**
 * Objects whose Might is mid-computation.
 *
 * `mightOf` consults passives, a passive can be gated on "while I am [Mighty]",
 * and that condition asks for Might again. Re-entry falls back to the printed
 * value rather than looping.
 */
const computingMight = new Set<ObjectId>();

/** The same guard for keywords, which auras can be filtered on. */
const computingKeywords = new Set<ObjectId>();

/**
 * Passives on `source` that are live right now, with `conditional` wrappers
 * (824, 828) resolved away. Every reader goes through this, so a gated passive
 * can never be applied unconditionally.
 */
/**
 * 471/472. How many points it actually takes to win right now.
 *
 * Not `config.victoryScore`: Aspirant's Climb raises the bar by one while it
 * is on the board, and with both players fielding one the target is ten rather
 * than eight.
 *
 * It lives here because three places need the same answer and had three
 * answers. The victory check used the modified target; the Final Point rule
 * (471.1.b) and the HUD both used the base one -- so the endgame restriction
 * started two points early, and a player sat watching "8 / 8" with the game
 * carrying on and nothing on screen to explain why.
 */
export function victoryTarget(state: GameState, rules: Rules): number {
  let target = state.config.victoryScore;
  for (const o of boardObjects(state)) {
    for (const e of activePassives(state, rules, o)) {
      if (e.p === 'victoryScoreMod') target += e.by;
    }
  }
  return target;
}

export function activePassives(
  state: GameState, rules: Rules, source: GameObject,
): PassiveEffect[] {
  const out: PassiveEffect[] = [];
  const visit = (effects: PassiveEffect[]): void => {
    for (const effect of effects) {
      if (effect.p !== 'conditional') { out.push(effect); continue; }
      const held = testStaticCondition(
        state, rules, source, source.controller, effect.cond,
      );
      // An unknown condition is treated as unmet: a passive that cannot be
      // judged must not silently apply for free.
      if (held === true) visit(effect.effects);
    }
  };
  for (const ability of abilitiesOf(rules, source.card)) {
    if (ability.kind === 'passive') visit(ability.effects);
  }
  return out;
}

/**
 * 355.9.b. Whether `obj` is off-limits to a chooser.
 *
 * Enforced in `candidates`, which every target selection goes through.
 */
/**
 * 206.1. An activated ability's resource cost right now, with any "costs [N]
 * less for each ..." reduction applied.
 *
 * Used both where abilities are offered and where they are paid, so the two can
 * never disagree about the price.
 */
export function abilityResourceCost(
  state: GameState, rules: Rules, source: GameObject,
  cost: {
    energy?: number; power?: number;
    energyLessPer?: { amount: number; from: DynamicAmount };
    energyLessIf?: { amount: number; cond: Condition };
  },
  /**
   * 827. Which keyword's price this is, when it is one. A discount can name
   * the keyword -- "[Empower] costs of your units here cost [1] less" -- and
   * nothing about a bare `ActivationCost` says which keyword it came from.
   */
  from?: 'empower' | 'equip',
  /**
   * 206. What this activation would choose. "This ability's Energy cost is
   * reduced by the Might of the unit you choose" is priced from the target,
   * which the board cannot answer -- only the pending activation knows.
   */
  chosen?: readonly ObjectId[],
): Cost {
  let energy = cost.energy ?? 0;
  let power = cost.power ?? 0;

  // 356. A once-a-turn discount on activated abilities, spent by whichever
  // matching ability goes first this turn.
  energy -= firstEachTurnDiscount(
    state, rules, source.controller, 'ability', rules.index.get(source.card),
  );

  if (from) {
    const pool = state.players[source.controller].pool;
    for (const other of boardObjects(state)) {
      for (const e of activePassives(state, rules, other)) {
        if (e.p !== 'abilityCostModifier' || e.from !== from) continue;
        if (!matchesFilter(state, rules, source, e.filter, other)) continue;
        if (!e.either) { energy -= e.energy; power -= e.power; continue; }
        // "[1] or [A]": the half the pool is short of, the same reading the
        // other either-costs take.
        const short = power > pool.any
          + DOMAINS.reduce((n, d) => n + pool.power[d], 0);
        if (short && e.power > 0) power -= e.power;
        else energy -= e.energy;
      }
    }
  }

  if (cost.energyLessPer) {
    const n = cost.energyLessPer.from === 'chosenMight'
      // Read from the activation's own target rather than from the board.
      ? (() => {
        const first = chosen?.[0] !== undefined ? state.objects[chosen[0]] : undefined;
        return first ? effectiveMight(state, rules, first) : 0;
      })()
      : countDynamic(state, rules, source, source.controller, cost.energyLessPer.from);
    energy -= cost.energyLessPer.amount * n;
  }
  if (cost.energyLessIf
    && testStaticCondition(
      state, rules, source, source.controller, cost.energyLessIf.cond) === true) {
    energy -= cost.energyLessIf.amount;
  }
  return {
    energy: Math.max(0, energy),
    power: Math.max(0, power),
    powerDomains: rules.index.get(source.card).domains,
  };
}

/**
 * 203.1. Objects that could pay a sacrifice cost, in a stable order.
 *
 * Shared by every payment path. It used to live in the engine, which meant the
 * interpreter's own `payTo` could not see it and quietly skipped the cost.
 */
/**
 * 203.1. Cards in hand that satisfy a discard cost.
 *
 * A filtered discard ("discard a spell") is a narrower price than a plain one,
 * so the same list has to decide both whether it can be paid and what pays it.
 */
export function discardCandidates(
  state: GameState, rules: Rules, player: PlayerId, filter: ObjectFilter | undefined,
): ObjectId[] {
  const hand = zoneList(state, 'hand', player);
  if (!filter) return [...hand];
  return hand.filter((id) => {
    const card = state.objects[id];
    return card !== undefined && matchesCardDef(rules.index.get(card.card), filter);
  });
}

export function sacrificeCandidates(
  state: GameState, rules: Rules, source: GameObject,
  sacrifice: { filter: ObjectFilter; count: number },
): GameObject[] {
  return boardObjects(state)
    .filter((o) => o.controller === source.controller)
    .filter((o) => matchesFilter(state, rules, o, sacrifice.filter, source))
    .sort((a, b) => a.id - b.id);
}

/**
 * Cost components any payment path knows how to settle.
 *
 * Three paths pay costs -- activating an ability, an additional cost while
 * playing, and a `payTo` mid-resolution -- and each grew its own list. A
 * component missing from one is paid in some places and silently skipped in
 * others, which is how "Recycle me to ready your runes" gave its effect for
 * free. The test asserts card data only uses what is listed here.
 */
export const PAYABLE_COST_PARTS = new Set<string>([
  'energy', 'power', 'powerDomain', 'exhaust', 'discard', 'discardFilter',
  'recycleFromTrash',
  'killSelf', 'recycleSelf', 'banishSelf', 'recycleFromTrashFilter', 'burn',
  'disempowerSelf',
  'spendBuff', 'spendEmpower', 'spendXp',
  'sacrifice', 'energyLessPer', 'energyLessIf', 'once',
]);

/**
 * 809. The extra Power a chooser owes for targeting things with Deflect.
 *
 * 809.1.c: "Spells and abilities an opponent controls that target me cost an
 * amount of Power equal to the Deflect Value more to play, for each time they
 * choose me." Only opponents pay it, and 809.1.c.1 says the Power may be of
 * any domain -- which is why it comes back as a domainless count.
 */
export function deflectCost(
  state: GameState, rules: Rules, targets: readonly ObjectId[], chooser: PlayerId,
): number {
  let owed = 0;
  for (const id of targets) {
    const o = state.objects[id];
    if (!o || o.controller === chooser) continue;
    // 809. A battlefield can lift the tax for everything chosen at its own
    // location -- for both players, which is why it is not filtered by side.
    const lifted = boardObjects(state).some((other) =>
      other.location !== null
      && sameLocation(other.location, o.location)
      && activePassives(state, rules, other).some((e) => e.p === 'noDeflectHere'));
    if (lifted) continue;
    owed += keywordAmount(state, rules, o, 'deflect');
  }
  return owed;
}

/**
 * 356. What a once-a-turn discount takes off, or zero.
 *
 * "The first friendly non-token gear played each turn costs [1] less" is spent
 * by whichever matching thing goes first, so the tally of what has already
 * happened this turn is what decides it -- not the board.
 */
export function firstEachTurnDiscount(
  state: GameState, rules: Rules, player: PlayerId,
  what: 'card' | 'ability', def: CardDef,
): number {
  const already = what === 'card'
    ? state.players[player].playedThisTurn
    : state.players[player].abilitiesUsedThisTurn;

  let off = 0;
  for (const source of boardObjects(state)) {
    for (const e of activePassives(state, rules, source)) {
      if (e.p !== 'firstEachTurnDiscount' || e.what !== what) continue;
      if (source.controller !== player) continue;
      if (!matchesCardDef(def, e.filter)) continue;
      // Spent once the first match has gone.
      if (already.some((id) => matchesCardDef(rules.index.get(id), e.filter))) continue;
      off += e.energy;
    }
  }
  return off;
}

/** 416. The cards in a player's trash a filtered recycle cost may take. */
export function recycleCandidates(
  state: GameState, rules: Rules, player: PlayerId, filter?: ObjectFilter,
): ObjectId[] {
  return zoneList(state, 'trash', player).filter((id) => {
    const o = state.objects[id];
    return o !== undefined
      && (!filter || matchesCardDef(rules.index.get(o.card), filter));
  });
}

/**
 * 356.2. What this player's own auras take off an optional additional cost.
 *
 * Read off the board rather than from the card: the discount belongs to a
 * permanent the player controls, and the card asking the extra price knows
 * nothing about it.
 */
export function extraCostDiscount(
  state: GameState, rules: Rules, player: PlayerId, price: { energy: number; power: number },
): { energy: number; power: number } {
  let energy = 0;
  let power = 0;
  const pool = state.players[player].pool;
  for (const source of boardObjects(state)) {
    if (source.controller !== player) continue;
    for (const e of activePassives(state, rules, source)) {
      if (e.p !== 'extraCostDiscount') continue;
      if (!e.either) { energy += e.energy; power += e.power; continue; }
      // "[1] or [A]": the Power half only when Power is what the player is
      // short of, which is the half they would actually take.
      const short = price.power - power > pool.any
        + DOMAINS.reduce((n, d) => n + pool.power[d], 0);
      if (short && e.power > 0) power += e.power;
      else energy += e.energy;
    }
  }
  return { energy, power };
}

/**
 * 356. "This costs [2] less if you choose a Bird, Cat, Dog, or Poro."
 *
 * A discount the *played card* carries, conditional on what it chooses -- so
 * unlike `chosenDiscount` (which the chosen object carries) it is read off the
 * card's own abilities, and unlike a plain `selfCostModifier` it cannot be
 * settled until targets exist.
 */
export function choosingDiscount(
  state: GameState, rules: Rules, cardId: string,
  targets: readonly ObjectId[], source: GameObject,
): { energy: number; power: number } {
  let energy = 0;
  let power = 0;
  for (const ability of abilitiesOf(rules, cardId)) {
    if (ability.kind !== 'passive') continue;
    for (const e of ability.effects) {
      if (e.p !== 'selfCostModifier' || !e.ifChoosing) continue;
      const hit = targets.some((id) => {
        const o = state.objects[id];
        return o !== undefined && matchesFilter(state, rules, o, e.ifChoosing!, source);
      });
      if (!hit) continue;
      energy += e.energy;
      power += e.power ?? 0;
    }
  }
  return { energy, power };
}

/**
 * 356. The discount the chosen objects hand the chooser, priced beside the
 * Deflect tax because both are settled by *which* things the play chose.
 *
 * Returns the reduction as a cost-shaped pair. `either` forms are resolved
 * here, where the pool is in view: the half the player can actually pay is the
 * half they would take.
 */
export function chosenDiscount(
  state: GameState, rules: Rules, targets: readonly ObjectId[], chooser: PlayerId,
  base: Cost,
): { energy: number; power: number } {
  let energy = 0;
  let power = 0;
  const pool = state.players[chooser].pool;
  // A discount can be held by the chosen object itself, or by a bystander that
  // has something to say about what was chosen -- a battlefield about the units
  // standing on it. Both are gathered here, keyed to the target that earns it.
  const holders: { holder: GameObject; target: GameObject }[] = [];
  for (const id of targets) {
    const o = state.objects[id];
    if (!o) continue;
    holders.push({ holder: o, target: o });
    for (const other of boardObjects(state)) {
      if (other.id === o.id) continue;
      if (activePassives(state, rules, other)
        .some((e) => e.p === 'chosenDiscount' && e.filter
          && matchesFilter(state, rules, o, e.filter, other))) {
        holders.push({ holder: other, target: o });
      }
    }
  }

  for (const { holder, target: o } of holders) {
    for (const e of activePassives(state, rules, holder)) {
      if (e.p !== 'chosenDiscount') continue;
      if (holder.id === o.id ? e.filter !== undefined : e.filter === undefined) continue;
      const friendly = o.controller === chooser;
      if (e.side === 'friendly' && !friendly) continue;
      if (e.side === 'enemy' && friendly) continue;
      if (!e.either) { energy += e.energy; power += e.power; continue; }
      // "[1] or [A] less": take the Power half only when the pool is short of
      // the Power the card costs and the Energy half would not help.
      const powerShort = base.power - power > pool.any
        + DOMAINS.reduce((n, d) => n + pool.power[d], 0);
      if (powerShort && e.power > 0) power += e.power;
      else energy += e.energy;
    }
  }
  return { energy, power };
}

/** 705. Whether damage can be put on `obj` at all. */
export function canBeDealtDamage(
  state: GameState, rules: Rules, obj: GameObject,
  /** Who the damage comes from, when an effect is dealing it. */
  dealer?: PlayerId,
): boolean {
  // `by` is about who is dealing it: an "enemy spells and abilities" shield
  // turns aside an opponent's effects and nothing else, so a caller that does
  // not say who is asking only sees the unconditional form.
  return !activePassives(state, rules, obj).some(
    (e) => e.p === 'cantBeDealtDamage'
      && (e.by === undefined
        || (e.by === 'enemyEffects' && dealer !== undefined && dealer !== obj.controller)),
  );
}

export function cantBeChosenBy(
  state: GameState, rules: Rules, obj: GameObject, chooser: PlayerId,
): boolean {
  for (const effect of activePassives(state, rules, obj)) {
    if (effect.p !== 'cantBeChosen' || effect.filter) continue;
    if (effect.by === 'any') return true;
    if (obj.controller !== chooser) return true;
  }
  // 355.7. And the same protection handed out by a neighbour, which is an aura
  // on that card rather than a passive on this one.
  for (const source of boardObjects(state)) {
    for (const effect of activePassives(state, rules, source)) {
      if (effect.p !== 'cantBeChosen' || !effect.filter) continue;
      if (!matchesFilter(state, rules, obj, effect.filter, source)) continue;
      if (effect.by === 'any') return true;
      if (obj.controller !== chooser) return true;
    }
  }
  // The same protection handed out for a turn rather than printed on the card.
  if (obj.mods.some((m) => m.kind === 'shield') && obj.controller !== chooser) return true;
  return false;
}

/**
 * 144. Whether an effect `mover` controls may move `obj` at all.
 *
 * Separate from `cantBeChosenBy`: a unit that cannot be moved can still be
 * chosen, dealt damage and killed -- only the relocation is refused.
 */
export function cantBeMovedBy(
  state: GameState, rules: Rules, obj: GameObject, mover: PlayerId,
): boolean {
  // 144. A prohibition handed out for the turn rather than printed on the card.
  // It stops the unit moving at all, including under its own steam, which is
  // what "they can't move it this turn" says.
  if (state.turnEffects.some((e) => e.k === 'cantMove' && e.object === obj.id)) return true;
  for (const effect of activePassives(state, rules, obj)) {
    if (effect.p !== 'cantBeMoved') continue;
    if (effect.by === 'any') return true;
    if (obj.controller !== mover) return true;
  }
  return false;
}

/**
 * Whether a standing prohibition blocks `what` for `player`.
 *
 * `subject` is the object the action concerns, when there is one -- a unit
 * being readied or moved -- so a restriction naming itself ("I can't be
 * readied") only catches that object.
 */
export function isRestricted(
  state: GameState, rules: Rules,
  what: 'playCards' | 'score' | 'ready' | 'moveToBase' | 'playUnitsHere'
    | 'revealHidden' | 'playNamedSpell',
  player: PlayerId,
  opts: { at?: Location | null; subject?: GameObject } = {},
): boolean {
  for (const source of boardObjects(state)) {
    for (const effect of activePassives(state, rules, source)) {
      if (effect.p !== 'restrict' || effect.what !== what) continue;

      // 470. "... until their third turn": lifted once that player has taken
      // that many, counted from their own side rather than the turn number.
      if (effect.untilTurn !== undefined
        && state.players[player].turnsTaken >= effect.untilTurn - 1) continue;

      if (effect.who === 'self') {
        if (!opts.subject || opts.subject.id !== source.id) continue;
      } else if (effect.who === 'you') {
        if (player !== source.controller) continue;
      } else if (effect.who === 'opponents') {
        if (player === source.controller) continue;
      }

      // 133.8. "Opponents can't play spells with that name": the restriction
      // only bites on the card the source actually named, and on nothing at
      // all until it has named one.
      if (effect.what === 'playNamedSpell') {
        if (!source.named) continue;
        if (!opts.subject) continue;
        if (rules.index.get(opts.subject.card).name !== source.named) continue;
      }

      // "here" is the source's own location -- a battlefield card restricting
      // what happens on it.
      if (effect.where === 'here') {
        const at = opts.at ?? opts.subject?.location ?? null;
        if (!source.location || !sameLocation(at, source.location)) continue;
      }
      return true;
    }
  }
  return false;
}

/**
 * The prohibitions `isRestricted` is actually asked about.
 *
 * A `restrict` kind nothing ever queries is the same silent failure as a
 * trigger nothing raises: the card compiles, counts as modelled, and stops
 * nothing at all. A test asserts the generated data only uses kinds listed
 * here, and every one of these has a call site.
 */
export const IMPLEMENTED_RESTRICTIONS = new Set<string>([
  'playCards', 'score', 'ready', 'moveToBase', 'playUnitsHere', 'revealHidden',
  'playNamedSpell',
]);

/**
 * Passive kinds the engine actually acts on.
 *
 * Seven kinds were once declared in the effect vocabulary and compiled into
 * card data while nothing read them, so those cards counted as modelled and
 * did nothing. A test asserts the generated data only uses kinds listed here,
 * which turns that class of mistake into a failing build.
 */
/**
 * Turn-scoped effect kinds the engine acts on. Same contract as
 * IMPLEMENTED_PASSIVES: emitting one that nothing reads is a silent no-op.
 */
export const IMPLEMENTED_TURN_EFFECTS = new Set<TurnEffect['k']>([
  'cantPlayCards', 'nextSpellBonusDamage', 'nextUnitEntersReady',
  'preventEffectDamage', 'nextDeath', 'nextDamage', 'nextUnitPlayed',
  'unitsEnterReady', 'buffBonus', 'anyDamageKills', 'nextSpellDiscount',
  'costThisTurn', 'onUnitPlayed', 'damageShield', 'damageMultiplier', 'cantMove',
  'nextSpellRepeat', 'winsCombat', 'grantedAbility',
  'freeHide', 'playFromTrash',
]);

/**
 * Trigger kinds the engine actually raises.
 *
 * Twenty-five of the thirty-six declared kinds were once compiled into card
 * data while nothing raised them: 104 cards counted as fully modelled and sat
 * inert. A test asserts the generated data only uses kinds listed here.
 */
/**
 * Ability kinds the engine acts on.
 *
 * `additionalCost` was compiled by 18 cards while nothing paid or offered it,
 * which is the same failure as the inert passives and triggers one level up.
 */
export const IMPLEMENTED_ABILITY_KINDS = new Set<string>([
  'triggered', 'activated', 'passive', 'spell', 'additionalCost',
]);

export const IMPLEMENTED_TRIGGERS = new Set<string>([
  'attacks', 'attacksOrDefends', 'conquer', 'defends', 'dies', 'endOfTurn',
  'hold', 'moves', 'played', 'startOfPhase',
  'playerPlaysCard', 'unitDies', 'youKillUnit', 'youDraw', 'youDiscard',
  'youRecycle', 'youStun', 'youEmpower', 'becomesEmpowered', 'youBuff',
  'becomesBuffed', 'youBanish', 'readied', 'endOfCombat', 'startOfCombat',
  'chosen', 'youAttachEquipment', 'playedFromHidden',
  'nthCardPlayed', 'enemyAttacksYourBattlefield', 'mightReaches',
  'unitBecomesMighty', 'score', 'opponentMoves',
  'youHide', 'youSpendBuff', 'youUseAbility', 'opponentScores', 'discarded',
  'revealed',
  'returnedToHand', 'unitMoves', 'youMoveUnit',
]);

export const IMPLEMENTED_PASSIVES = new Set<PassiveEffect['p']>([
  'mightAura', 'keywordAura', 'costModifier', 'selfCostModifier',
  'entersExhausted', 'entersReady',
  'cantBeChosen', 'conditional', 'selfKeyword', 'selfMight', 'selfMightFrom',
  'noCombatDamage', 'abilityRequiresBattlefield', 'playableTo', 'restrict',
  'multiBuff', 'bonusDamage', 'bonusMightPenalty', 'replaceDeath', 'replaceChosen',
  'gainsAbilitiesOf', 'duplicateTokens', 'moveTax', 'grantsAdditionalCost',
  'peekBeforeReveal', 'skipDraw', 'trashBecomesBanish',
  'cantBeDealtDamage', 'hideCost',
  'extraScoreTrigger', 'extraKeywordTrigger', 'extraHideSlot', 'openToMoves',
  'holdAndConquerAlias',
  'ignoreKeywordInCombat', 'cantBeCountered', 'grantsAbility', 'suppressKeyword',
  'cantBeMoved', 'empowerLimit', 'channelCap', 'chosenDiscount', 'extraCostDiscount',
  'grantsRepeat', 'repeatCostModifier', 'ignoreDeflect', 'replaceScore',
  'unlimitedCopies', 'flowCostModifier', 'noDeflectHere', 'abilityCostModifier',
  'replaceTie',
  'firstEachTurnDiscount',
  'selfTag',
  'extraResource', 'victoryScoreMod', 'anyDamageKills', 'cantBeReadied',
  'attachedMightMultiplier',
  'cantPlayBeforeTurn',
]);

/** 706. Extra damage every Deal by `player` carries, from standing effects. */
export function bonusDamageFor(
  state: GameState, rules: Rules, player: PlayerId,
): number {
  let bonus = 0;
  for (const source of boardObjects(state)) {
    if (source.controller !== player) continue;
    for (const effect of activePassives(state, rules, source)) {
      if (effect.p === 'bonusDamage') bonus += effect.amount;
    }
  }
  return bonus;
}

/**
 * A filter applied to a printed card rather than to something on the board.
 *
 * Only the fields that make sense without a game object are consulted: type,
 * tags and printed cost. Board state -- location, damage, statuses -- cannot
 * be asked of a card that is no longer anywhere.
 */
/** Whether a printed type answers to one of the grammar's type words (141). */
export function matchesTypeWord(
  t: CardDef['type'], word: NonNullable<ObjectFilter['type']>,
): boolean {
  if (word === 'unit') return isUnitType(t);
  if (word === 'spell') return t === 'spell' || t === 'signatureSpell';
  return t === word;
}

export function matchesCardDef(def: CardDef, filter: ObjectFilter): boolean {
  if (filter.type) {
    const t = def.type;
    const ok = filter.type === 'unit' ? isUnitType(t)
      : filter.type === 'spell' ? (t === 'spell' || t === 'signatureSpell')
        : t === filter.type;
    if (!ok) return false;
  }
  if (filter.notType && matchesTypeWord(def.type, filter.notType)) return false;
  if (filter.isToken && def.type !== 'token') return false;
  if (filter.notToken && def.type === 'token') return false;
  if (filter.champion && def.superType !== 'champion') return false;
  if (filter.card && def.id !== filter.card) return false;
  if (filter.tag && !def.tags.includes(filter.tag)) return false;
  if (filter.notTag && def.tags.includes(filter.notTag)) return false;
  if (filter.maxEnergyCost !== undefined && def.cost.energy > filter.maxEnergyCost) return false;
  if (filter.minEnergyCost !== undefined && def.cost.energy < filter.minEnergyCost) return false;
  return true;
}

interface AuraHit { source: GameObject; effect: PassiveEffect }

/** Every passive on the board whose filter currently matches `obj`. */
export function aurasAffecting(state: GameState, rules: Rules, obj: GameObject): AuraHit[] {
  const hits: AuraHit[] = [];
  for (const source of boardObjects(state)) {
    for (const effect of activePassives(state, rules, source)) {
      if (effect.p !== 'mightAura' && effect.p !== 'keywordAura') continue;
      if (matchesFilter(state, rules, obj, effect.filter, source)) {
        hits.push({ source, effect });
      }
    }
  }
  return hits;
}

export function matchesFilter(
  state: GameState, rules: Rules, obj: GameObject,
  filter: ObjectFilter, source: GameObject,
  /**
   * What the effect has already chosen, when there is one. Some filters
   * compare against it rather than against the source -- "with less [M] than
   * it" -- and without it those can never be true, so they reject instead.
   */
  chosen?: GameObject,
): boolean {
  const def = rules.index.get(obj.card);

  if (filter.other && obj.id === source.id) return false;
  if (filter.anyOf && !filter.anyOf.some((f) => matchesFilter(state, rules, obj, f, source))) {
    return false;
  }
  // 133.8. Against every tag the object has, not only the printed ones: an
  // object can gain a tag, and `matchesCardDef` below can only see the card.
  if (filter.tag && !tagsOf(rules, obj).includes(filter.tag)) return false;
  // 133.8. "A unit with the named tag" -- the word the source named as it was
  // played. A source that has not named one matches nothing rather than
  // everything: an unnamed List must not debuff the whole board.
  // 144. Either end of the source's last move. A source that has not moved
  // matches nothing rather than everything.
  if (filter.atMoveEndpoints) {
    const ends = [source.location, source.movedFrom];
    if (!ends.some((at) => at && sameLocation(obj.location, at))) return false;
  }
  if (filter.namedTag
    && (!source.named || !tagsOf(rules, obj).includes(source.named))) return false;
  if (filter.notTag && tagsOf(rules, obj).includes(filter.notTag)) return false;
  if (filter.attacking && !obj.statuses.includes('attacking')) return false;
  if (filter.defending && !obj.statuses.includes('defending')) return false;
  if (filter.equipped && obj.attachments.length === 0) return false;
  if (filter.lessMightThanSource
    && effectiveMight(state, rules, obj) >= effectiveMight(state, rules, source)) {
    return false;
  }
  if (filter.lessMightThanChosen
    && (!chosen || effectiveMight(state, rules, obj) >= effectiveMight(state, rules, chosen))) {
    return false;
  }
  if (filter.atChosenLocation
    && (!chosen || !sameLocation(obj.location, chosen.location))) {
    return false;
  }
  if (filter.inShowdown
    && !obj.statuses.includes('attacking') && !obj.statuses.includes('defending')) {
    return false;
  }
  if (filter.unitsThere !== undefined) {
    const at = obj.location;
    const here = at === null ? 0 : boardObjects(state).filter((o) =>
      o.id !== obj.id && sameLocation(o.location, at)
      && isUnitType(rules.index.get(o.card).type)).length;
    if (here !== filter.unitsThere) return false;
  }
  if (filter.whereYouHaveUnits || filter.occupied) {
    // 152. A battlefield described by who is standing at it. `obj` is the
    // battlefield card, so the question is about its own index.
    const at = obj.location;
    const here = at === null ? [] : boardObjects(state).filter((o) =>
      o.id !== obj.id && sameLocation(o.location, at)
      && isUnitType(rules.index.get(o.card).type));
    if (filter.occupied && here.length === 0) return false;
    if (filter.whereYouHaveUnits
      && !here.some((o) => o.controller === source.controller)) return false;
  }
  if (filter.alone) {
    const at = obj.location;
    const others = at === null ? [] : boardObjects(state).filter((o) =>
      o.id !== obj.id && sameLocation(o.location, at)
      && isUnitType(rules.index.get(o.card).type));
    if (others.length > 0) return false;
  }
  if (filter.defendsAlone) {
    if (!obj.statuses.includes('defending')) return false;
    const others = boardObjects(state).filter((o) =>
      o.id !== obj.id && o.controller === obj.controller
      && o.statuses.includes('defending'));
    if (others.length > 0) return false;
  }
  if (filter.attached && obj.attachedTo === null) return false;
  if (filter.detached && obj.attachedTo !== null) return false;
  if (filter.damaged && obj.damage <= 0) return false;
  if (filter.isToken && def.type !== 'token') return false;
  if (filter.notToken && def.type === 'token') return false;
  if (filter.champion && def.superType !== 'champion') return false;
  if (filter.card && def.id !== filter.card) return false;
  if (filter.sameNameAsSource && def.name !== rules.index.get(source.card).name) return false;
  if (filter.domain && !def.domains.includes(filter.domain)) return false;
  if (filter.playedFromNonHand
    && (obj.playedFrom === null || obj.playedFrom === 'hand')) return false;
  // A filter naming a zone reaches cards that are not on the board at all,
  // which is what "each Equipment in your hand" describes.
  if (filter.zone && obj.zone.kind !== filter.zone) return false;
  // 447. "Banished with me": the same card did the banishing, not merely
  // another copy of it -- two Virtuosos each count only their own spells.
  if (filter.banishedBySelf && obj.banishedBy !== source.id) return false;
  // 818. "If I would die" on an Equipment is about the unit it is attached to.
  if (filter.host && source.attachedTo !== obj.id) return false;
  // 137. Without a referent this can never be true, so it rejects rather than
  // quietly matching everything.
  if (filter.sameControllerAsChosen && obj.controller !== chosen?.controller) return false;
  if (filter.isChosen && obj.id !== chosen?.id) return false;
  // 152. Everyone at this place, taken together, against the source.
  if (filter.weakerThanSourceTogether) {
    const at = obj.location;
    if (!at) return false;
    let total = 0;
    for (const o of boardObjects(state)) {
      if (o.controller === source.controller) continue;
      if (!isUnitType(rules.index.get(o.card).type)) continue;
      if (!sameLocation(o.location, at)) continue;
      total += effectiveMight(state, rules, o);
    }
    if (effectiveMight(state, rules, source) <= total) return false;
  }

  // 355.9. Whether anything pending on the chain has chosen this.
  if (filter.chosenBy) {
    const picked = state.chain.some((item) => {
      if (!item.targets.includes(obj.id)) return false;
      const head = state.objects[item.source];
      return head !== undefined
        && matchesFilter(state, rules, head, filter.chosenBy!, source, chosen);
    });
    if (!picked) return false;
  }

  // 463. Who this unit is facing: someone matching, on the other side, in the
  // same combat -- which is to say at the same battlefield with a designation.
  if (filter.inCombatWith) {
    const at = obj.location;
    const fighting = obj.statuses.includes('attacking') || obj.statuses.includes('defending');
    if (!at || !fighting) return false;
    const facing = boardObjects(state).some((o) =>
      o.controller !== obj.controller
      && sameLocation(o.location, at)
      && (o.statuses.includes('attacking') || o.statuses.includes('defending'))
      && matchesFilter(state, rules, o, filter.inCombatWith!, source, chosen));
    if (!facing) return false;
  }
  if (filter.maxMightOverChosen !== undefined) {
    if (!chosen) return false;
    const ceiling = effectiveMight(state, rules, chosen) + filter.maxMightOverChosen;
    if (effectiveMight(state, rules, obj) > ceiling) return false;
  }
  // 206. Both halves of the price, against the referent's printed cost.
  if (filter.costsNoMoreThanChosen) {
    if (!chosen) return false;
    const mine = rules.index.get(obj.card).cost;
    const theirs = rules.index.get(chosen.card).cost;
    if (mine.energy > theirs.energy || mine.power > theirs.power) return false;
  }
  // 152. A destination judged by its occupants: somewhere the referent's own
  // controller already has a unit, which is what "the same controller" means
  // when the referent is the unit being moved.
  if (filter.whereUnitSharesControllerWithChosen) {
    const at = obj.location;
    if (!at || !chosen) return false;
    const shared = boardObjects(state).some((o) =>
      o.id !== chosen.id
      && o.controller === chosen.controller
      && isUnitType(rules.index.get(o.card).type)
      && sameLocation(o.location, at));
    if (!shared) return false;
  }

  // 327. What is on the chain, and what it chose. Both are questions about the
  // pending item rather than about the object heading it.
  if (filter.onChain || filter.chooses) {
    const item = state.chain.find((i) => i.source === obj.id);
    if (!item) return false;
    if (filter.onChain === 'spell' && item.kind !== 'spell') return false;
    if (filter.onChain === 'ability' && item.kind !== 'ability') return false;
    const picks = item.targets
      .map((id) => state.objects[id])
      .filter((o): o is GameObject => Boolean(o));
    if (filter.chooses
      && !picks.some((o) => matchesFilter(state, rules, o, filter.chooses!, source, chosen))) {
      return false;
    }
    // "... and no other friendly unit": nothing else it picked may match.
    if (filter.choosesNoOther
      && picks.some((o) => o.id !== chosen?.id
        && matchesFilter(state, rules, o, filter.choosesNoOther!, source, chosen))) {
      return false;
    }
  }

  if (filter.type) {
    const t = def.type;
    const ok = filter.type === 'unit' ? isUnitType(t)
      : filter.type === 'gear' ? t === 'gear'
        : filter.type === 'permanent' ? isUnitType(t) || t === 'gear'
          : filter.type === 'rune' ? t === 'rune'
            : filter.type === 'battlefield' ? t === 'battlefield'
              : filter.type === 'legend' ? t === 'legend'
                : t === 'spell' || t === 'signatureSpell';
    if (!ok) return false;
  }
  if (filter.notType && matchesTypeWord(def.type, filter.notType)) return false;

  if (filter.ownedByYou && obj.owner !== source.controller) return false;
  if (filter.side === 'friendly' && obj.controller !== source.controller) return false;
  if (filter.side === 'enemy' && obj.controller === source.controller) return false;

  if (filter.where === 'here' && !sameLocation(obj.location, source.location)) return false;
  // 144. "at a different location" -- anywhere the source is not, which for a
  // card with no location of its own means nowhere qualifies.
  if (filter.where === 'elsewhere'
    && (!source.location || sameLocation(obj.location, source.location))) return false;
  if (filter.where === 'battlefield' && obj.location?.kind !== 'battlefield') return false;
  if (filter.where === 'base' && obj.location?.kind !== 'base') return false;

  if (filter.exhausted && !obj.exhausted) return false;
  if (filter.ready && obj.exhausted) return false;
  if (filter.stunned && !obj.statuses.includes('stunned')) return false;
  if (filter.buffed && !obj.statuses.includes('buffed')) return false;
  if (filter.empowered && !obj.statuses.includes('empowered')) return false;
  if (filter.tag && def.tag !== filter.tag) return false;
  if (filter.domain && !def.domains.includes(filter.domain)) return false;
  if (filter.hasKeyword && !hasKeywordNow(state, rules, obj, filter.hasKeyword)) return false;
  if (filter.lacksKeyword && hasKeywordNow(state, rules, obj, filter.lacksKeyword)) return false;

  if (filter.minMight !== undefined && mightOf(state, rules, obj) < filter.minMight) return false;
  if (filter.maxMight !== undefined && mightOf(state, rules, obj) > filter.maxMight) return false;
  if (filter.maxEnergyCost !== undefined && def.cost.energy > filter.maxEnergyCost) return false;
  if (filter.maxEnergyCostFrom !== undefined) {
    const cap = countDynamic(state, rules, source, source.controller, filter.maxEnergyCostFrom);
    if (def.cost.energy > cap) return false;
  }
  if (filter.minEnergyCost !== undefined && def.cost.energy < filter.minEnergyCost) return false;
  if (filter.maxPowerCost !== undefined && def.cost.power > filter.maxPowerCost) return false;

  return true;
}

// -- might -------------------------------------------------------------------

/** 701. A buff counter is worth +1 Might. */
export const BUFF_MIGHT = 1;

/**
 * 143.2. A unit's Might right now: printed, plus modifications, its buff
 * counter, any Might auras, attached Might bonuses, and the Assault/Shield
 * bonuses that apply only while it carries a combat designation.
 *
 * The raw value can go negative; callers that need the value used for damage
 * and comparisons should use `effectiveMight`, which clamps at 0 (143.2.b).
 */
export function mightOf(state: GameState, rules: Rules, obj: GameObject): number {
  const def = rules.index.get(obj.card);
  if (def.might === null) return 0;

  let might = def.might;

  for (const m of obj.mods) {
    if (m.kind === 'might') might += m.amount;
  }
  if (obj.statuses.includes('buffed')) {
    // 137.2. Every buff it carries, not just the fact of one.
    might += BUFF_MIGHT * Math.max(1, obj.buffCount ?? 1);
    // 426. "Buffs give an additional +1 [M] to friendly units this turn."
    for (const e of state.turnEffects) {
      if (e.k === 'buffBonus' && e.player === obj.controller) might += e.amount;
    }
  }

  // 143.2. Re-entry: this object's Might is already being computed further up
  // the stack. Two units can each carry a passive gated on the other being
  // [Mighty], and following that round would never terminate, so the inner
  // answer is the printed value plus its own modifiers -- everything that
  // needs no other object's Might to know.
  if (computingMight.has(obj.id)) return might;
  computingMight.add(obj.id);
  try {
    return mightWithEffects(state, rules, obj, might);
  } finally {
    computingMight.delete(obj.id);
  }
}

/** The part of `mightOf` that has to consult other objects. */
function mightWithEffects(
  state: GameState, rules: Rules, obj: GameObject, base: number,
): number {
  let might = base;

  for (const { effect } of aurasAffecting(state, rules, obj)) {
    if (effect.p !== 'mightAura') continue;
    // "to a minimum of N [M]" floors the reduction rather than the total, so a
    // unit already below the floor is not raised to it.
    if (effect.min !== undefined && effect.amount < 0) {
      might += Math.max(effect.amount, effect.min - might);
    } else {
      might += effect.amount;
    }
  }

  // "I have +2 [M]", and Might that tracks a changing quantity.
  for (const effect of activePassives(state, rules, obj)) {
    if (effect.p === 'selfMight') might += effect.amount;
    else if (effect.p === 'selfMightFrom') {
      might += countDynamic(state, rules, obj, obj.controller, effect.from)
        * (effect.per ?? 1);
    }
  }

  // 137.3. An attached card's Might Bonus modulates its host. A host can print
  // a multiplier on that -- "each Equipment attached to me gives double its
  // base Might bonus" -- which scales each attachment's contribution, not the
  // host's own Might, so it cannot be a `mightAura`.
  let attachedFactor = 1;
  for (const ability of abilitiesOf(rules, obj.card)) {
    if (ability.kind !== 'passive') continue;
    for (const e of ability.effects) {
      if (e.p === 'attachedMightMultiplier') attachedFactor *= e.factor;
    }
  }
  // 137.3.b. "If the card to which a card with a Might Bonus is Attached has
  // no Might value, the Might Bonus is ignored." A gear carrying a gear adds
  // nothing to anything.
  if (rules.index.get(obj.card).might !== null) {
    for (const id of obj.attachments) {
      const att = state.objects[id];
      if (!att) continue;
      // 137.1 / 718.4. The number in the attached card's lower right corner --
      // its *Might Bonus* -- not its own Might.
      //
      // This read `attDef.might`, which every one of the 40 printed Equipment
      // leaves null: a Gear has no Might of its own. So the branch could never
      // be taken and no Equipment had ever modulated anything. Long Sword said
      // +2 on its face, was paid for, attached correctly, and the unit it was
      // strapped to fought at its printed Might.
      const bonus = rules.index.get(att.card).mightBonus;
      if (bonus !== null) might += bonus * attachedFactor;
    }
  }

  // 807 / 814. Assault applies only to attackers, Shield only to defenders.
  if (obj.statuses.includes('attacking')) {
    might += keywordAmount(state, rules, obj, 'assault');
  }
  if (obj.statuses.includes('defending')) {
    might += keywordAmount(state, rules, obj, 'shield');
  }

  return might;
}

/** 143.2.b. Might is treated as 0 when it would be negative. */
export const effectiveMight = (state: GameState, rules: Rules, obj: GameObject): number =>
  Math.max(0, mightOf(state, rules, obj));

/** 142.4.b. Lethal damage is non-zero damage at or above the unit's Might. */
export function hasLethalDamage(state: GameState, rules: Rules, obj: GameObject): boolean {
  if (!isUnitType(rules.index.get(obj.card).type)) return false;
  if (obj.damage <= 0) return false;
  // 705. "When any unit takes damage this turn, kill it": any damage at all is
  // lethal while that is in force, whoever put it there.
  if (state.turnEffects.some((e) => e.k === 'anyDamageKills')) return true;
  // 705. And the printed form of the same thing, which names whose units it
  // catches rather than applying to the whole board.
  for (const source of boardObjects(state)) {
    for (const e of activePassives(state, rules, source)) {
      if (e.p !== 'anyDamageKills') continue;
      if (matchesFilter(state, rules, obj, e.filter, source)) return true;
    }
  }
  return obj.damage >= effectiveMight(state, rules, obj);
}

// -- costs -------------------------------------------------------------------

/**
 * 356. A card's cost after passive modifiers. Base cost modifications from
 * passives on the board are applied here; the printed cost stays available for
 * every effect that asks what a card "costs" (206).
 */
export function totalCost(state: GameState, rules: Rules, def: CardDef, controller: PlayerId): Cost {
  let energy = def.cost.energy;
  const power = def.cost.power;

  // 356. The card's own discount, which applies while it is still in hand and
  // so cannot be found by walking the board.
  let powerOff = 0;
  for (const ability of abilitiesOf(rules, def.id)) {
    if (ability.kind !== 'passive') continue;
    for (const effect of selfCostEffects(state, rules, controller, ability.effects)) {
      // Both of these are applied by the play machinery, which is the only
      // thing that knows whether the extra cost was taken and where the card
      // is being played from.
      // All three are settled by the play machinery: it is the only thing that
      // knows whether the extra cost was taken, where the card is played from,
      // and what this play chooses.
      if (effect.ifPaidAdditional || effect.ifNotFromHand || effect.ifChoosing) continue;
      const per = effect.per
        // The card is not an object yet, so only counts that read the player's
        // zones and board are answerable; a self-referential count is 0.
        ? countDynamic(state, rules, null, controller, effect.per)
        : 1;
      // 356. The floor belongs to this modifier, applied as it is taken.
      energy = effect.min !== undefined
        ? Math.max(effect.min, energy - effect.energy * per)
        : energy - effect.energy * per;
      powerOff += (effect.power ?? 0) * per;
    }
  }

  for (const source of boardObjects(state)) {
    if (source.controller !== controller) continue;
    // Through `activePassives`, not the raw ability list: a modifier gated by
    // "while I'm at a battlefield" is wrapped in a `conditional`, and reading
    // the effects flat walks straight past it.
    for (const effect of activePassives(state, rules, source)) {
      {
        if (effect.p !== 'costModifier') continue;
        // A modifier with an empty filter is the source discounting itself.
        const selfOnly = Object.keys(effect.filter).length === 0;
        if (selfOnly) {
          if (source.card !== def.id) continue;
        } else if (!matchesCardDef(def, effect.filter)) {
          // The filter was carried in the data but never tested, so "your
          // spells cost [1] less" discounted units and gear as well.
          continue;
        }
        // 356. A floor is per modifier, applied as the modifier is taken, so
        // two discounts with different floors do not share one.
        const off = effect.energy ?? 0;
        energy = effect.min !== undefined
          ? Math.max(effect.min, energy + off)
          : energy + off;
        // "[1][A] less" discounts a Power pip as well, and reading only the
        // Energy half would ship the card cheaper on paper than in play.
        powerOff -= effect.power ?? 0;
      }
    }
  }

  // 356. Turn-scoped cost changes, which are not on any permanent.
  for (const e of state.turnEffects) {
    if (e.k !== 'costThisTurn' || e.player !== controller) continue;
    if (!matchesCardDef(def, e.filter)) continue;
    energy += e.energy;
  }

  return {
    energy: Math.max(0, energy),
    power: Math.max(0, power - powerOff),
    // 135.2.e. `powerDomains` is the set of domains the Power pips may be paid
    // in, not a list with one entry per pip -- and an empty set means "any".
    // Shortening it alongside the count turns a Calm-only cost into one any
    // domain can pay, so a discount leaves it exactly as printed.
    powerDomains: def.cost.powerDomains,
  };
}

/**
 * 356.2. What a card's cost drops by when its optional additional cost is
 * taken. Clamped by the caller against the card's actual cost.
 */
/**
 * 356.2. The discount a card takes from the card killed to pay its extra cost.
 *
 * Separate from `additionalCostDiscount` because the number is not on the card
 * being played: it is the printed cost of the victim, which is only known once
 * the price has actually been paid.
 */
export function sacrificedDiscount(
  rules: Rules, cardId: string, victims: readonly GameObject[],
): { energy: number; power: number } {
  let energy = 0;
  let power = 0;
  for (const a of abilitiesOf(rules, cardId)) {
    if (a.kind !== 'passive') continue;
    for (const e of a.effects) {
      if (e.p !== 'selfCostModifier' || !e.perSacrificed) continue;
      for (const victim of victims) {
        const cost = rules.index.get(victim.card).cost;
        energy += e.energy * cost.energy;
        power += (e.power ?? 0) * cost.power;
      }
    }
  }
  return { energy, power };
}

export function additionalCostDiscount(
  rules: Rules, cardId: string,
): { energy: number; power: number } {
  return conditionalSelfDiscount(rules, cardId, 'ifPaidAdditional');
}

/** 356. The discount for playing this card from anywhere but its owner's hand. */
export function nonHandDiscount(
  rules: Rules, cardId: string,
): { energy: number; power: number } {
  return conditionalSelfDiscount(rules, cardId, 'ifNotFromHand');
}

function conditionalSelfDiscount(
  rules: Rules, cardId: string, flag: 'ifPaidAdditional' | 'ifNotFromHand',
): { energy: number; power: number } {
  let energy = 0;
  let power = 0;
  for (const ability of abilitiesOf(rules, cardId)) {
    if (ability.kind !== 'passive') continue;
    for (const effect of ability.effects) {
      if (effect.p !== 'selfCostModifier' || !effect[flag]) continue;
      energy += effect.energy;
      power += effect.power ?? 0;
    }
  }
  return { energy, power };
}

/**
 * The self-discounts in `effects` that currently apply, flattening the
 * `conditional` wrapper that Level abilities compile to.
 *
 * A card in hand has no object, so a gate about "me" cannot be judged and the
 * discount does not apply -- which is the safe direction: the card costs full
 * price rather than being wrongly cheap.
 */
function selfCostEffects(
  state: GameState, rules: Rules, controller: PlayerId, effects: PassiveEffect[],
): Extract<PassiveEffect, { p: 'selfCostModifier' }>[] {
  const out: Extract<PassiveEffect, { p: 'selfCostModifier' }>[] = [];
  for (const effect of effects) {
    if (effect.p === 'selfCostModifier') { out.push(effect); continue; }
    if (effect.p !== 'conditional') continue;
    if (testStaticCondition(state, rules, null, controller, effect.cond) !== true) continue;
    out.push(...selfCostEffects(state, rules, controller, effect.effects));
  }
  return out;
}

/**
 * Whether a pool can pay a cost. Power pips are [C] -- payable by any of the
 * card's own domains -- and fall back to [A] when the card has none
 * (135.2.e.6.b). Universal Power in the pool can pay any pip.
 */
/** Whether restricted Energy tagged `only` may pay for `purpose`. */
export function restrictionAllows(
  only: SpendOnly, purpose: SpendPurpose, inShowdown: boolean,
): boolean {
  switch (only) {
    case 'spells': return purpose === 'spell';
    case 'units': return purpose === 'unit';
    case 'gear': return purpose === 'gear';
    case 'unitsOrUnitAbilities': return purpose === 'unit' || purpose === 'unitAbility';
    case 'gearOrGearAbilities': return purpose === 'gear' || purpose === 'gearAbility';
    case 'showdowns': return inShowdown;
    default: return false;
  }
}

/** Restricted Energy in `pool` that is usable for this payment. */
export function usableRestricted(
  pool: RunePool, purpose: SpendPurpose = 'other', inShowdown = false,
): number {
  let n = 0;
  for (const r of pool.restricted) {
    if (restrictionAllows(r.only, purpose, inShowdown)) n += r.energy;
  }
  return n;
}

export function canPay(
  pool: RunePool, cost: Cost, purpose: SpendPurpose = 'other', inShowdown = false,
): boolean {
  if (pool.energy + usableRestricted(pool, purpose, inShowdown) < cost.energy) return false;
  if (cost.power === 0) return true;

  const domains = cost.powerDomains.length > 0 ? cost.powerDomains : DOMAINS;
  let available = pool.any;
  for (const d of domains) available += pool.power[d];
  return available >= cost.power;
}

/**
 * Spends a cost from a pool. Domain-specific Power is spent before universal
 * Power, so [A] stays available for costs that only it can pay.
 */
export function pay(
  pool: RunePool, cost: Cost, purpose: SpendPurpose = 'other', inShowdown = false,
): boolean {
  if (!canPay(pool, cost, purpose, inShowdown)) return false;

  // Restricted Energy goes first: it is worth nothing once the pool empties,
  // so spending free Energy while it sits there would waste it.
  let owed = cost.energy;
  for (const r of pool.restricted) {
    if (owed === 0) break;
    if (!restrictionAllows(r.only, purpose, inShowdown)) continue;
    const take = Math.min(r.energy, owed);
    r.energy -= take;
    owed -= take;
  }
  pool.restricted = pool.restricted.filter((r) => r.energy > 0);
  pool.energy -= owed;

  let remaining = cost.power;
  const domains: readonly Domain[] = cost.powerDomains.length > 0 ? cost.powerDomains : DOMAINS;
  for (const d of domains) {
    if (remaining === 0) break;
    const take = Math.min(pool.power[d], remaining);
    pool.power[d] -= take;
    remaining -= take;
  }
  if (remaining > 0) {
    const take = Math.min(pool.any, remaining);
    pool.any -= take;
    remaining -= take;
  }
  return remaining === 0;
}

/** Total resources sitting in a pool, for AI evaluation and display. */
export const poolSize = (pool: RunePool): number =>
  pool.energy + pool.any + DOMAINS.reduce((a, d) => a + pool.power[d], 0);

// -- battlefields ------------------------------------------------------------

export const battlefield = (state: GameState, i: number): BattlefieldState => {
  const bf = state.battlefields[i];
  if (!bf) throw new Error(`no battlefield ${i}`);
  return bf;
};

/** 190.4.a. A player controls a battlefield while their units are the only ones there. */
export function soleOccupant(
  state: GameState, rules: Rules, index: number,
): PlayerId | null {
  const units = unitsAt(state, rules.index, battlefieldLocation(index));
  if (units.length === 0) return null;
  const first = units[0]!.controller;
  return units.every((u) => u.controller === first) ? first : null;
}

export function playersAt(state: GameState, rules: Rules, index: number): PlayerId[] {
  const seen = new Set<PlayerId>();
  for (const u of unitsAt(state, rules.index, battlefieldLocation(index))) seen.add(u.controller);
  return [...seen];
}
